import { Navigate, useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import {
  canPayTourDocument,
  canSignTourAddendum,
  isPaymentDeadlineExpired,
  isTourDocumentCustomer,
  useTourDocument,
} from 'entities/tour/models/document'

import { PageLoader } from 'shared/ui/page-loader'

import { DocumentState } from '../../ui/document-state'

import { AddendumCard } from './ui/addendum-card'
import { BookingStatus } from './ui/booking-status'
import { ContractCard } from './ui/contract-card'
import { ManagerCard } from './ui/manager-card'
import { PaymentCard } from './ui/payment-card'
import { PaymentPanel } from './ui/payment-panel'
import { TourSummary } from './ui/tour-summary'
import { TouristsCard } from './ui/tourists-card'

import s from './styles.module.scss'

const DocumentDetailsPage = () => {
  const navigate = useNavigate()
  const { documentId } = useParams()
  const {
    data: document,
    isLoading,
    isError,
    refetch,
  } = useTourDocument(documentId)

  const handleGoBack = () => navigate(-1)
  const handleOpenPaymentHistory = () => {
    if (documentId) {
      navigate(
        RoutingPaths.tours.documentDetails.paymentHistory.path(documentId),
      )
    }
  }
  const handleOpenTourists = () => {
    if (documentId) {
      navigate(RoutingPaths.tours.documentDetails.tourists.path(documentId))
    }
  }

  if (document) {
    const isCustomer = isTourDocumentCustomer(document)

    if (isCustomer && canSignTourAddendum(document)) {
      return (
        <Navigate
          to={RoutingPaths.tours.contract.path(document.id)}
          replace
        />
      )
    }

    const hasPaymentRemainder =
      document.remainingAmount !== null && document.remainingAmount > 0
    const isPaymentExpired =
      hasPaymentRemainder &&
      isPaymentDeadlineExpired(document.fullPaymentBeforeDate)
    const shouldShowPaymentPanel = canPayTourDocument(document)

    return (
      <main
        className={clsx(
          'safe-area-top-padding',
          s.page,
          shouldShowPaymentPanel && s.pageWithPaymentPanel,
        )}
      >
        <div
          className={s.content}
          key={documentId}
        >
          <TourSummary
            document={document}
            onGoBack={handleGoBack}
          />
          <BookingStatus status={document.status} />
          {isCustomer && document.status !== 'CANCELLED' && (
            <ContractCard document={document} />
          )}
          {isCustomer && document.status !== 'CANCELLED' && (
            <AddendumCard document={document} />
          )}
          {isCustomer && (
            <PaymentCard
              document={document}
              isPaymentExpired={isPaymentExpired}
              onOpenHistory={handleOpenPaymentHistory}
            />
          )}
          <TouristsCard
            document={document}
            onOpenAll={handleOpenTourists}
          />
          <ManagerCard document={document} />
        </div>
        {shouldShowPaymentPanel && (
          <PaymentPanel
            document={document}
            hasRefreshError={isError}
            onPayPart={() =>
              navigate(
                RoutingPaths.tours.documentDetails.partialPayment.path(
                  document.id,
                ),
              )
            }
          />
        )}
      </main>
    )
  }

  if (documentId && isLoading) return <PageLoader />

  if (!documentId || isError) {
    return (
      <DocumentState
        onGoBack={handleGoBack}
        onRetry={() => void refetch()}
      />
    )
  }

  return null
}
export default DocumentDetailsPage
